import type { PaletteColor } from "@brexhq/metal-foundation";
import isPropValid from "@emotion/is-prop-valid";
import type { CSSObject, Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin as polishedMargin, padding as polishedPadding } from "polished";
import type { CSSProperties } from "react";

import { convertSpacersToNumericArray } from "@brexhq/next/src/components/layout/Spacers/Spacers";
import type {
  GapSpacingProp,
  SpacingProp,
} from "@brexhq/next/src/components/layout/Spacers/types";
import type { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import type { SpacingKeys } from "@brexhq/shared/styles/spacing";
import { type BorderRadii } from "@brexhq/shared/styles/theming";

type ResponsiveStyle<T> = Partial<Record<BreakPointVals, T>>;

export type PolishedSpacing = [
  SpacingKeys?,
  SpacingKeys?,
  SpacingKeys?,
  SpacingKeys?,
];

type CommonProps = {
  debug?: boolean;
  margin?: SpacingProp;
  padding?: SpacingProp;
  gap?: GapSpacingProp;
};

type NormalPropNames =
  | "minHeight"
  | "height"
  | "maxHeight"
  | "width"
  | "minWidth"
  | "maxWidth"
  | "overflowX"
  | "overflowY"
  | "justifyContent"
  | "alignItems"
  | "textAlign"
  | "position"
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "display"
  | "flexWrap"
  | "order"
  | "flex";

type BaseProps = Pick<CSSProperties, NormalPropNames> & {
  backgroundColor?: PaletteColor | CSSProperties["backgroundColor"];
  borderRadius?: BorderRadii | CSSProperties["borderRadius"];
  // Specifically use `textColor` instead of `color` to avoid inheriting the types from `HTMLDivElement`.
  // This value gets applied to `color` in styles below
  textColor?: PaletteColor | CSSProperties["color"];
  direction?: CSSProperties["flexDirection"];
};

// style function only accepts responsive style props
type StyleProps = {
  [key in keyof BaseProps]: ResponsiveStyle<BaseProps[key]>;
} & CommonProps;

// Container props can be either responsive style or not
export type ContainerProps = {
  [key in keyof BaseProps]: BaseProps[key] | ResponsiveStyle<BaseProps[key]>;
} & CommonProps;

const styles = (
  breakpoint: BreakPointVals,
  theme: Theme,
  props: StyleProps,
): CSSObject => {
  const {
    margin,
    padding,
    gap,
    direction,
    backgroundColor = {},
    height = {},
    width = {},
    justifyContent = {},
    alignItems = {},
    overflowX = {},
    overflowY = {},
    borderRadius = {},
    position = {},
    maxWidth = {},
    maxHeight = {},
    minHeight = {},
    minWidth = {},
    top = {},
    bottom = {},
    left = {},
    right = {},
    display = {},
    flexWrap = {},
    order = {},
    flex = {},
    textAlign = {},
    textColor = {},
  } = props;
  const actualJustify = justifyContent[breakpoint];
  const actualAlign = alignItems[breakpoint];
  const actualFlexDirection = direction?.[breakpoint];

  // If alignment is set or direction is set, then flex position the child
  const alignmentStyles: CSSObject =
    actualJustify || actualAlign || actualFlexDirection
      ? {
          display: display[breakpoint] ?? "flex",
          flexDirection: actualFlexDirection ?? "row",
          alignItems: actualAlign ?? "initial",
          justifyContent: actualJustify ?? "initial",
        }
      : { display: display[breakpoint] ?? "block" };

  const borderRadiusAtBreakpoint = borderRadius[breakpoint];
  const actualBorderRadius =
    borderRadiusAtBreakpoint && theme.radius[borderRadiusAtBreakpoint]
      ? theme.radius[borderRadiusAtBreakpoint]
      : borderRadius[breakpoint];

  return {
    ...alignmentStyles,
    ...polishedMargin(...convertSpacersToNumericArray(margin, breakpoint)),
    ...polishedPadding(...convertSpacersToNumericArray(padding, breakpoint)),
    gap: gap
      ? convertSpacersToNumericArray(gap, breakpoint).join("px ") + "px"
      : "0",
    overflowX: overflowX[breakpoint],
    overflowY: overflowY[breakpoint],
    color: textColor[breakpoint],
    backgroundColor: backgroundColor[breakpoint],
    height: height[breakpoint],
    width: width[breakpoint],
    borderRadius: actualBorderRadius,
    position: position[breakpoint],
    maxWidth: maxWidth[breakpoint],
    maxHeight: maxHeight[breakpoint],
    minWidth: minWidth[breakpoint],
    minHeight: minHeight[breakpoint],
    top: top[breakpoint],
    left: left[breakpoint],
    right: right[breakpoint],
    bottom: bottom[breakpoint],
    flexWrap: flexWrap[breakpoint],
    order: order[breakpoint],
    flex: flex[breakpoint],
    textAlign: textAlign[breakpoint],
    // in safari this fixes border radius not showing
    WebkitMaskImage: actualBorderRadius
      ? "-webkit-radial-gradient(white, black)"
      : "initial",
  };
};

// converts all props that are not responsive into a responsive object
const makeAllPropsResponsiveStyleObjects = (
  props: ContainerProps,
  exceptions?: string[],
): StyleProps => {
  const styleFormattedProps = {};
  for (const element in props) {
    if (
      !exceptions?.includes[element] &&
      !Object.prototype.hasOwnProperty.call(props[element] ?? {}, "LRG") &&
      !Object.prototype.hasOwnProperty.call(props[element] ?? {}, "MED") &&
      !Object.prototype.hasOwnProperty.call(props[element] ?? {}, "SML")
    ) {
      styleFormattedProps[element] = {
        LRG: props[element],
        MED: props[element],
        SML: props[element],
      };
    } else {
      styleFormattedProps[element] = props[element];
    }
  }
  return styleFormattedProps;
};

/**
 * Spacing Scale Tokens:
 * {@link desktop/tablet SpacingScale}
 * Types can be anything in BaseProps or a Responsive Version of them:
 * {@link BaseProps}
 */

export const Container = styled("div", {
  shouldForwardProp: (prop) =>
    isPropValid(prop as string) &&
    prop !== "height" &&
    prop !== "width" &&
    prop !== "direction" &&
    prop !== "display",
})<ContainerProps>((props) => {
  const { theme, debug } = props;
  const { MED, SML } = theme.breakpoints;
  const filteredProps = makeAllPropsResponsiveStyleObjects(props, [
    "debug",
    "children",
  ]);

  return {
    outline: debug ? "1px solid lightblue" : "",
    ...styles("LRG", theme, filteredProps),
    [MED]: styles("MED", theme, filteredProps),
    [SML]: styles("SML", theme, filteredProps),
  };
});
